import { create } from 'zustand';
import { captureEvent } from '@/lib/analytics/posthog';
import { Id, Doc } from '@/convex/_generated/dataModel';
import type { FunctionReference, OptionalRestArgs, DefaultFunctionArgs } from 'convex/server';
import { createScraperStripeCheckoutSession } from '@/app/actions/createScraperStripeCheckoutSession';
import { calculateEffectiveMinimumQuantity } from '@/lib/stripeMinimumUtils';

interface PriceRange {
  min?: number;
  max?: number;
}

// Helper type for Convex mutation function
type ConvexMutationFn<Args extends DefaultFunctionArgs, Output> = (
  ...args: OptionalRestArgs<FunctionReference<'mutation', 'public', Args, Output>>
) => Promise<Output>;

// Helper type for the server action
type CreateStripeSessionAction = typeof createScraperStripeCheckoutSession;

// Explicit type for scraper order creation arguments
interface CreateScraperOrderArgs extends DefaultFunctionArgs {
  scraperId: Id<'scrapers'>;
  requirements: string;
  stripeSessionId: string;
  paymentIntentId: string;
  userId: string; // Changed from buyerUserId to match the mutation parameter
  providerId: string; // Added providerId parameter
  price: number; // Added price parameter
  quantity: number;
}

interface ScraperState {
  // Pagination
  currentPage: number;
  setCurrentPage: (page: number) => void;

  // Sorting
  sortBy: string;
  setSortBy: (sortBy: string) => void;

  // Search
  searchTerm: string;
  setSearchTerm: (term: string) => void;

  // Filters
  sourcePlatforms: string[];
  setSourcePlatforms: (platforms: string[]) => void;

  serviceType: string | undefined;
  setServiceType: (type: string | undefined) => void;

  priceRange: PriceRange;
  setPriceRange: (range: PriceRange) => void;

  deliveryTime: string | undefined;
  setDeliveryTime: (time: string | undefined) => void;

  providerRating: number | undefined;
  setProviderRating: (rating: number | undefined) => void;

  // Scraper Detail State
  currentScraperId: Id<'scrapers'> | null;
  currentScraper: Doc<'scrapers'> | null;
  currentProvider: Doc<'users'> | null;
  currentReviews: Doc<'scraperReviews'>[] | null;
  averageRating: number;
  requirements: string;
  orderQuantity: number;
  minOrderQuantity: number;
  isOrderModalOpen: boolean;
  isSubmitting: boolean;

  // Reset all filters
  resetFilters: () => void;

  // Scraper Detail Actions
  setCurrentScraperId: (id: Id<'scrapers'> | null) => void;
  setCurrentScraper: (scraper: Doc<'scrapers'> | null) => void;
  setCurrentProvider: (provider: Doc<'users'> | null) => void;
  setCurrentReviews: (reviews: Doc<'scraperReviews'>[] | null) => void;
  setRequirements: (req: string) => void;
  setOrderQuantity: (quantity: number) => void;
  setMinOrderQuantity: (min: number) => void;
  toggleOrderModal: (isOpen: boolean) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  handleOrderSubmit: (
    isSignedIn: boolean,
    buyerUserId: string | null,
    createScraperOrderMutation: ConvexMutationFn<CreateScraperOrderArgs, Id<'scraperOrders'>>,
    stripeCheckoutAction: CreateStripeSessionAction,
  ) => Promise<void>;
  calculateAverageRating: () => void;
  resetDetailState: () => void;
}

// Helper function to track events in PostHog
const trackEvent = (
  eventName: string,
  properties?: Record<string, string | number | boolean | string[] | null | undefined>,
) => {
  captureEvent(eventName, properties);
};

export const useScraperStore = create<ScraperState>((set, get) => ({
  // Default values
  currentPage: 1,
  sortBy: 'recommended',
  searchTerm: '',
  sourcePlatforms: [],
  serviceType: undefined,
  priceRange: {},
  deliveryTime: undefined,
  providerRating: undefined,
  currentScraperId: null,
  currentScraper: null,
  currentProvider: null,
  currentReviews: null,
  averageRating: 0,
  requirements: '',
  orderQuantity: 1,
  minOrderQuantity: 1,
  isOrderModalOpen: false,
  isSubmitting: false,

  // Actions
  setCurrentPage: (page) => {
    trackEvent('scraper_pagination', { page });
    set({ currentPage: page });
  },

  setSortBy: (sortBy) => {
    trackEvent('scraper_sort', { sortBy });
    set({ sortBy });
  },

  setSearchTerm: (searchTerm) => {
    trackEvent('scraper_search', { term: searchTerm });
    set({ searchTerm, currentPage: 1 });
  },

  setSourcePlatforms: (sourcePlatforms) => {
    trackEvent('scraper_filter_platforms', { platforms: sourcePlatforms });
    set({ sourcePlatforms, currentPage: 1 });
  },

  setServiceType: (serviceType) => {
    trackEvent('scraper_filter_service_type', { serviceType: serviceType || 'all' });
    set({ serviceType, currentPage: 1 });
  },

  setPriceRange: (priceRange) => {
    trackEvent('scraper_filter_price', { min: priceRange.min, max: priceRange.max });
    set({ priceRange, currentPage: 1 });
  },

  setDeliveryTime: (deliveryTime) => {
    trackEvent('scraper_filter_delivery_time', { deliveryTime });
    set({ deliveryTime, currentPage: 1 });
  },

  setProviderRating: (providerRating) => {
    trackEvent('scraper_filter_provider_rating', { rating: providerRating });
    set({ providerRating, currentPage: 1 });
  },

  setCurrentScraperId: (id) => {
    set({ currentScraperId: id });
  },

  setCurrentScraper: (scraper) => {
    if (!scraper) {
      set({
        currentScraper: null,
        minOrderQuantity: 1,
        orderQuantity: 1,
      });
      return;
    }

    const scraperMinQuantity = scraper.minOrderQuantity || 1;
    const effectiveMinQuantity = calculateEffectiveMinimumQuantity(
      scraper.price,
      scraperMinQuantity,
    );

    set({
      currentScraper: scraper,
      minOrderQuantity: effectiveMinQuantity,
      orderQuantity: effectiveMinQuantity, // Initialize with effective minimum
    });
  },

  setCurrentProvider: (provider) => {
    set({ currentProvider: provider });
  },

  setCurrentReviews: (reviews) => {
    set({ currentReviews: reviews });
    get().calculateAverageRating();
  },

  setRequirements: (req) => {
    set({ requirements: req });
  },

  setOrderQuantity: (quantity) => {
    const { minOrderQuantity } = get();
    trackEvent('scraper_quantity_changed', { quantity });
    set({ orderQuantity: Math.max(minOrderQuantity, quantity) });
  },

  setMinOrderQuantity: (min) => {
    set({ minOrderQuantity: min });
  },

  toggleOrderModal: (isOpen) => {
    trackEvent('scraper_toggle_order_modal', { isOpen });
    set({ isOrderModalOpen: isOpen });
    // Reset quantity when opening modal to minimum
    if (isOpen) {
      const { minOrderQuantity } = get();
      set({ orderQuantity: minOrderQuantity });
    }
  },

  setIsSubmitting: (isSubmitting) => {
    set({ isSubmitting });
  },

  calculateAverageRating: () => {
    const { currentReviews } = get();
    if (currentReviews && currentReviews.length > 0) {
      const avgRating =
        currentReviews.reduce((sum, review) => sum + review.rating, 0) / currentReviews.length;
      set({ averageRating: avgRating });
    } else {
      set({ averageRating: 0 });
    }
  },

  handleOrderSubmit: async (
    isSignedIn,
    buyerUserId,
    createScraperOrderMutation,
    stripeCheckoutAction,
  ) => {
    const {
      currentScraperId,
      requirements,
      currentScraper,
      orderQuantity,
      minOrderQuantity,
      setIsSubmitting,
      toggleOrderModal,
    } = get();

    if (!isSignedIn) {
      alert('Please sign in to place an order.');
      return;
    }

    if (!buyerUserId) {
      alert('User information not found. Please sign in again.');
      setIsSubmitting(false);
      return;
    }

    if (!currentScraperId || !currentScraper) {
      alert('Scraper details not loaded. Please try again.');
      return;
    }

    if (!requirements.trim()) {
      alert('Please provide your requirements.');
      return;
    }

    // Check minimum order quantity
    if (orderQuantity < minOrderQuantity) {
      const scraperMinQuantity = currentScraper.minOrderQuantity || 1;
      const reason =
        minOrderQuantity > scraperMinQuantity
          ? `Due to payment processing requirements, minimum order is ${minOrderQuantity} leads (£0.50 minimum total).`
          : `Minimum order quantity is ${minOrderQuantity} leads.`;
      alert(reason);
      return;
    }

    setIsSubmitting(true);
    trackEvent('scraper_place_order_initiated', {
      scraperId: currentScraperId,
      buyerUserId,
      quantity: orderQuantity,
    });

    try {
      const stripeSessionData = await stripeCheckoutAction({
        scraperId: currentScraperId,
        requirements,
        quantity: orderQuantity,
      });

      if (!stripeSessionData || !stripeSessionData.sessionId || !stripeSessionData.sessionUrl) {
        throw new Error('Failed to create Stripe session.');
      }

      trackEvent('scraper_stripe_session_created', {
        scraperId: currentScraperId,
        stripeSessionId: stripeSessionData.sessionId,
        quantity: orderQuantity,
      });

      await createScraperOrderMutation({
        scraperId: currentScraperId,
        requirements: requirements,
        stripeSessionId: stripeSessionData.sessionId,
        paymentIntentId: stripeSessionData.paymentIntentId || 'N/A',
        userId: buyerUserId, // Changed from buyerUserId to userId to match the expected parameter
        providerId: currentScraper.userId, // Added providerId from the scraper (using userId field)
        price: currentScraper.price * orderQuantity, // Added price calculation
        quantity: orderQuantity,
      });

      trackEvent('scraper_order_created_in_convex', {
        scraperId: currentScraperId,
        stripeSessionId: stripeSessionData.sessionId,
        buyerUserId,
        quantity: orderQuantity,
      });

      toggleOrderModal(false);
      window.location.href = stripeSessionData.sessionUrl;
    } catch (error) {
      console.error('Error during order submission:', error);
      let errorMessage = 'There was an error processing your order. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      alert(errorMessage);
      trackEvent('scraper_order_failed', {
        scraperId: currentScraperId,
        buyerUserId,
        quantity: orderQuantity,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  },

  resetDetailState: () => {
    set({
      currentScraperId: null,
      currentScraper: null,
      currentProvider: null,
      currentReviews: null,
      averageRating: 0,
      requirements: '',
      orderQuantity: 1,
      minOrderQuantity: 1,
      isOrderModalOpen: false,
      isSubmitting: false,
    });
  },

  // Reset all filters
  resetFilters: () => {
    trackEvent('scraper_reset_filters');
    set({
      currentPage: 1,
      sortBy: 'recommended',
      searchTerm: '',
      sourcePlatforms: [],
      serviceType: undefined,
      priceRange: {},
      deliveryTime: undefined,
      providerRating: undefined,
    });
  },
}));
